import * as React from "react";
import { Layers } from "lucide-react";
import { cn } from "@/lib/utils";
import { type ChatBinding, type ChatSource } from "@/lib/api";
import { buildCitationMap, renderMarkdown } from "@/lib/markdown";
import { FeedbackWidget, isServerMessageId } from "./FeedbackWidget";
import { NuanceComposer } from "./NuanceComposer";
import { ChatStepStrip } from "./ChatStepStrip";
import { type UIStep } from "@/lib/chat-steps";

export type Message = {
  id: string;
  role: "user" | "assistant";
  content: string;
  sources?: ChatSource[];
  // DDQ responses are stored as one message with kind='ddq_response'
  // and a structured payload. ChatPage detects this kind and renders
  // the DDQ accordion instead of MessageBubble.
  kind?: "text" | "ddq_response";
  structured_payload?: unknown;
};

export function MessageBubble({
  message,
  sources,
  onCitationClick,
  // WS5 — reasoning steps for the ACTIVE streaming assistant bubble only. Empty
  // (the default) for every other bubble and for the flag-OFF prod path, where
  // the empty-assistant branch falls back to today's typing-dots unchanged.
  steps = [],
  streaming = false,
  // WS-T4 — whether a trace exists for this message (to show the Reasoning affordance).
  hasTrace = false,
  // WS-T4 — callback to show this message's trace (opens the console + sets lastTrace).
  onShowTrace,
  // #145 — in-pane nuance capture. gp_team only; every prop below is what the
  // composer needs to prefill a governed golden with zero analyst input, and all
  // of it is already client-side (the fund switcher, the entitled-funds catalog,
  // the chat turn, and the `final-sources` SSE event). Absent ⇒ no composer.
  role,
  fund,
  fundName,
  question,
  bindings,
}: {
  message: Message;
  sources: ChatSource[] | undefined;
  onCitationClick: (sourceIndex: number) => void;
  steps?: UIStep[];
  streaming?: boolean;
  hasTrace?: boolean;
  onShowTrace?: () => void;
  role?: "gp_team" | "lp" | null;
  fund?: string | null;
  fundName?: string | null;
  question?: string | null;
  bindings?: ChatBinding[];
}) {
  const isUser = message.role === "user";
  const citationMap = React.useMemo(() => buildCitationMap(sources), [sources]);
  const html = React.useMemo(
    () => (isUser ? null : renderMarkdown(message.content, citationMap)),
    [isUser, message.content, citationMap],
  );

  // Click-handler delegation on the message body so generated citation
  // buttons stay keyboard-accessible without per-render re-binding.
  const onBodyClick = React.useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      const target = (e.target as HTMLElement).closest<HTMLElement>("[data-source]");
      if (!target) return;
      const idx = Number(target.dataset.source);
      if (Number.isFinite(idx)) onCitationClick(idx);
    },
    [onCitationClick],
  );

  // Show a typing-dots indicator when the assistant bubble has been
  // appended but no tokens have streamed in yet.
  const isEmptyAssistant = !isUser && (message.content || "").trim().length === 0;

  // Feedback can only attach to a server-persisted message (the endpoint looks
  // the row up by id and verifies ownership). Optimistic streaming bubbles use
  // `assistant-<ts>` ids; the real UUID arrives when the conversation is
  // reloaded. So the widget renders for persisted assistant answers only.
  const showFeedback = !isUser && !isEmptyAssistant && isServerMessageId(message.id);

  // #145 — the nuance composer is a GP-internal governance surface. It renders
  // ONLY for a gp_team session, and only where feedback can attach at all (a
  // server-persisted answer). An LP session must never see it.
  const showNuance =
    showFeedback && role === "gp_team" && !!fund && !!(question || "").trim();

  return (
    <div className={cn("flex", isUser ? "justify-end" : "justify-start")}>
      <div
        className={cn(
          "message-bubble rounded-md px-4 py-3 text-sm leading-6 shadow-sm",
          isUser
            ? "max-w-[88%] bg-primary text-primary-foreground sm:max-w-[78%]"
            : "max-w-full border bg-card text-card-foreground sm:max-w-[78%]",
        )}
      >
        {isUser ? (
          <p className="whitespace-pre-wrap">{message.content}</p>
        ) : isEmptyAssistant ? (
          // Empty streaming assistant bubble: show the WS5 reasoning-steps strip
          // when the worker streamed steps; otherwise fall back to today's
          // three typing-dots EXACTLY (the flag-OFF prod path — no steps arrive).
          steps.length > 0 ? (
            <ChatStepStrip steps={steps} streaming />
          ) : (
            <span className="inline-flex items-center gap-2 text-muted-foreground">
              <span className="size-1.5 animate-pulse rounded-full bg-muted-foreground/70" />
              <span className="size-1.5 animate-pulse rounded-full bg-muted-foreground/70 [animation-delay:120ms]" />
              <span className="size-1.5 animate-pulse rounded-full bg-muted-foreground/70 [animation-delay:240ms]" />
            </span>
          )
        ) : (
          <>
            {/* Post-answer: keep the steps as a collapsed "Reasoning" disclosure
                above the prose (default collapsed once the answer streams in,
                expanded while still streaming). Absent on the flag-OFF path. */}
            {steps.length > 0 ? (
              <ChatStepStrip steps={steps} streaming={streaming} />
            ) : null}
            <div
              className="prose-chat"
              onClick={onBodyClick}
              dangerouslySetInnerHTML={{ __html: html ?? "" }}
            />
          </>
        )}
        {showFeedback || hasTrace || showNuance ? (
          <div className="mt-3 border-t pt-2">
            <div className="flex flex-wrap items-center gap-1.5">
              {/* WS-T4 — Reasoning affordance: show when a trace exists for this message.
                  Clicking opens the console + loads that message's trace. */}
              {hasTrace && onShowTrace ? (
                <button
                  type="button"
                  className="inline-flex items-center gap-1 rounded-md px-1.5 py-1 text-[11px] font-medium text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
                  title="Show reasoning trace"
                  onClick={onShowTrace}
                >
                  <Layers className="size-[13px]" aria-hidden />
                  <span>Reasoning</span>
                </button>
              ) : null}
              {showFeedback ? <FeedbackWidget messageId={message.id} /> : null}
              {showNuance ? (
                <NuanceComposer
                  messageId={message.id}
                  fund={fund as string}
                  fundName={fundName}
                  question={question as string}
                  answerBody={message.content}
                  bindings={bindings}
                  sources={sources}
                />
              ) : null}
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
}
